import type { Element, ElementContent, Root, RootContent } from 'hast';
import {
  blockDirection,
  isLtrUnit,
  splitLtrRuns,
  type TextDirection,
} from './text-direction';

/** Blocks that get an explicit `dir` from their own dominant script. */
const BLOCKS = new Set([
  'p',
  'li',
  'ul',
  'ol',
  'td',
  'th',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'blockquote',
  'table',
]);

/** Inline elements isolated as one unit when their text is LTR: a link or a code span is never split. */
const WHOLE = new Set(['a', 'code']);

/** Inline formatting whose text is split into runs like the text around it. */
const INLINE = new Set([
  'strong',
  'em',
  'b',
  'i',
  'u',
  's',
  'del',
  'ins',
  'mark',
  'small',
  'span',
  'sub',
  'sup',
]);

function textOf(node: Root | RootContent): string {
  if (node.type === 'text') return node.value;
  if (node.type === 'element' || node.type === 'root')
    return node.children.map(textOf).join('');
  return '';
}

/** The block's prose: its text without link labels and code. */
function proseOf(node: Root | RootContent): string {
  if (node.type === 'text') return node.value;
  if (node.type === 'element') {
    if (
      node.tagName === 'a' ||
      node.tagName === 'code' ||
      node.tagName === 'pre'
    )
      return ' ';
    return node.children.map(proseOf).join('');
  }
  if (node.type === 'root') return node.children.map(proseOf).join('');
  return '';
}

/**
 * The elements on the path to the last non-blank text: the blocks still being
 * typed while an answer reveals.
 */
function openPath(node: Root | Element, path: Element[] = []): Element[] {
  for (let i = node.children.length - 1; i >= 0; i -= 1) {
    const child = node.children[i];
    if (child.type === 'text' && child.value.trim()) return path;
    if (child.type === 'element' && textOf(child).trim())
      return openPath(child, [...path, child]);
  }
  return path;
}

function ltrIsolate(children: ElementContent[]): Element {
  return {
    type: 'element',
    tagName: 'bdi',
    properties: { dir: 'ltr' },
    children,
  };
}

/** Wraps the LTR runs among an RTL element's inline content in `<bdi dir="ltr">`. */
function isolateRuns(parent: Element): void {
  parent.children = parent.children.flatMap((child): ElementContent[] => {
    if (child.type === 'text') {
      return splitLtrRuns(child.value).map((segment) =>
        segment.ltr
          ? ltrIsolate([{ type: 'text', value: segment.text }])
          : { type: 'text', value: segment.text }
      );
    }
    if (child.type !== 'element') return [child];
    if (WHOLE.has(child.tagName)) {
      if (isLtrUnit(textOf(child))) return [ltrIsolate([child])];
      // An Arabic link keeps its anchor whole and isolates the runs inside it.
      if (child.tagName === 'a') isolateRuns(child);
      return [child];
    }
    if (INLINE.has(child.tagName)) isolateRuns(child);
    // Nested blocks (a paragraph in a loose list item) are handled on their own.
    return [child];
  });
}

export interface RehypeBidiOptions {
  /** Off for a reply with no Arabic at all: its markup is left exactly as it was. */
  enabled: boolean;
  /** The whole reply's direction, from its FINAL content. */
  reply?: TextDirection;
  /** The answer is still typing out: its open blocks follow the reply. */
  revealing?: boolean;
}

function directBlocks(
  parent: Root | Element,
  options: RehypeBidiOptions,
  open: ReadonlySet<Element>
): void {
  for (const child of parent.children) {
    if (child.type !== 'element') continue;
    if (child.tagName === 'pre') {
      // Code reads left to right, whatever the item it sits in.
      child.properties = { ...child.properties, dir: 'ltr' };
      continue;
    }
    if (BLOCKS.has(child.tagName)) {
      const dir =
        open.has(child) && options.reply
          ? options.reply
          : blockDirection(textOf(child), proseOf(child), options.reply);
      if (dir) child.properties = { ...child.properties, dir };
      if (dir === 'rtl' && child.tagName !== 'table') isolateRuns(child);
    }
    directBlocks(child, options, open);
  }
}

/**
 * Bidi for replies that mix Arabic and English. Each block (paragraph, list,
 * list item, table, cell, heading, quote) gets an explicit `dir` by the word
 * rule in text-direction.ts, every code block is ltr, and inside RTL blocks
 * every run of English, digits and identifier punctuation is isolated, so
 * "QA-MULTI Lead A" or "17:30 UTC" can no longer reorder the Arabic around it.
 *
 * While an answer types out, the blocks still being typed take the reply's
 * direction, so a paragraph that starts with English does not flip mid-reveal.
 *
 * Runs AFTER rehype-sanitize, so sanitize cannot strip the `<bdi>`/`dir` it adds
 * (its default schema has no `bdi`) and still cleans everything the model wrote.
 */
export function rehypeBidi(options: RehypeBidiOptions) {
  return (tree: Root) => {
    if (!options.enabled) return;
    const open = new Set(options.revealing ? openPath(tree) : []);
    directBlocks(tree, options, open);
  };
}
